import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, Check, KeyRound } from 'lucide-react'
import { WizardStepper } from '@/components/shared/WizardStepper'

const STEPS = ['Account Details', 'Interview Types']

const schema = z.object({
  customer_name:   z.string().min(2),
  company_name:    z.string().min(2),
  email:           z.string().email(),
  username:        z.string().min(3),
  phone:           z.string().optional(),
  contact_email:   z.string().email(),
  video_interview: z.boolean().default(false),
  text_interview:  z.boolean().default(false),
  form_interview:  z.boolean().default(false),
})

type FormValues = z.infer<typeof schema>

const STEP_FIELDS: Array<(keyof FormValues)[]> = [
  ['customer_name', 'company_name', 'email', 'username', 'contact_email'],
  [],
]

export function CreateGreenhouseApiKeyPage() {
  const [step, setStep] = useState(0)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [serverError, setServerError] = useState('')

  const {
    register, handleSubmit, trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) as any })

  const handleNext = async () => {
    const valid = await trigger(STEP_FIELDS[step])
    if (valid) setStep((s) => s + 1)
  }

  const onSubmit = async (values: FormValues) => {
    setServerError('')
    try {
      const { data } = await flaskApi.post('/greenhouse/api-key', values)
      setApiKey(data.api_key)
    } catch (err: unknown) {
      setServerError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? 'Failed to create API key'
      )
    }
  }

  const handleCopy = () => {
    if (!apiKey) return
    navigator.clipboard.writeText(apiKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (apiKey) {
    return (
      <div className="w-full max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">API Key Created</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Store this key safely — it will not be shown again.</p>
        </div>
        <div className="bento-card p-8 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-[#076698]/10 dark:bg-sky-900/40 flex items-center justify-center text-[#076698]">
              <KeyRound size={20} />
            </div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">Your Greenhouse API Key</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-border/40 font-mono text-sm break-all">
            <span className="flex-1 min-w-0 break-all text-slate-700 dark:text-slate-200">{apiKey}</span>
            <Button variant="ghost" size="icon" onClick={handleCopy} className="shrink-0">
              {copied
                ? <Check size={14} className="text-emerald-600" />
                : <Copy size={14} />}
            </Button>
          </div>
          <Button onClick={() => { setApiKey(null); setStep(0) }} variant="outline">
            Create Another
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-800 dark:text-slate-50">Create Greenhouse API Key</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Provision a new Greenhouse integration API key.</p>
      </div>

      <div className="bento-card p-7 space-y-7">
        <div className="pb-6 border-b border-border/40">
          <WizardStepper steps={STEPS} current={step} />
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {serverError && (
            <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
              <span className="shrink-0 mt-0.5">⚠</span>
              <span>{serverError}</span>
            </div>
          )}

          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { name: 'customer_name' as const, label: 'Customer Name' },
                { name: 'company_name' as const,  label: 'Company Name' },
                { name: 'email' as const,         label: 'Email',         type: 'email' },
                { name: 'username' as const,      label: 'Username' },
                { name: 'phone' as const,         label: 'Phone' },
                { name: 'contact_email' as const, label: 'Contact Email', type: 'email' },
              ].map(({ name, label, type }) => (
                <div key={name} className="space-y-1.5">
                  <label className="text-xs font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</label>
                  <Input {...register(name)} type={type ?? 'text'} />
                  {errors[name] && (
                    <p className="text-xs text-destructive">{errors[name]?.message}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Select which interview types this integration should support.
              </p>
              <div className="space-y-3 p-5 rounded-xl bg-slate-50 dark:bg-slate-900 border border-border/40">
                {[
                  { name: 'video_interview' as const, label: 'Video Interview' },
                  { name: 'text_interview' as const,  label: 'Text Interview' },
                  { name: 'form_interview' as const,  label: 'Form Interview' },
                ].map(({ name, label }) => (
                  <label key={name} className="flex items-center gap-3 text-sm cursor-pointer font-medium">
                    <input type="checkbox" {...register(name)} className="rounded" />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mt-7 pt-5 border-t border-border/40">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep((s) => s - 1)}
              disabled={step === 0}
            >
              ← Back
            </Button>
            <span className="text-[10px] font-bold text-slate-400 text-center uppercase tracking-widest">
              Step {step + 1} of {STEPS.length}
            </span>
            {step < STEPS.length - 1 ? (
              <Button
                type="button"
                className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                onClick={handleNext}
              >
                Continue →
              </Button>
            ) : (
              <Button
                type="submit"
                className="bg-[#076698] hover:bg-[#055580] transition-colors duration-150"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create API Key'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
